import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { filterClients } from '../utils/clientSearch';
import { mostrarErrorAltaCliente, avisarAltaCliente } from '../utils/clientDisplay';
import apiService, { api, clientService } from '../services/api';
import { FaSync, FaEdit, FaTrash } from 'react-icons/fa';

const ClientsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
  }

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  @media (min-width: 1024px) {
    gap: 1rem;
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  white-space: nowrap;

  &:hover {
    background-color: #45a049;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const SyncButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  white-space: nowrap;

  &:hover {
    background-color: #1976D2;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    gap: 0.5rem;
  }
`;

const SearchBar = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 300px;
    margin-right: 1rem;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (min-width: 768px) {
    gap: 0.75rem;
  }

  @media (min-width: 1024px) {
    gap: 1rem;
  }
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
  }
`;

const ClientCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const ClientHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ClientName = styled.h3`
  color: #333;
  margin: 0;
  flex: 1 1 auto;
  word-break: break-word;
  font-size: 1rem;

  @media (min-width: 768px) {
    font-size: 1.17rem;
  }
`;

const ClientInfo = styled.div`
  color: #666;
  font-size: 0.85rem;
  margin: 0.2rem 0;

  @media (min-width: 768px) {
    font-size: 0.9rem;
    margin: 0.25rem 0;
  }
`;

const BadgesContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (min-width: 500px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-end;
  }
`;

const MarginBadge = styled.span`
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;

  @media (min-width: 768px) {
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
  }
`;

const ClientTypeBadge = styled.span`
  background-color: ${props => props.$isProfessional ? '#e8f5e9' : '#fff3e0'};
  color: ${props => props.$isProfessional ? '#2e7d32' : '#e65100'};
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;

  @media (min-width: 768px) {
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const EditButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
    color: #333;
  }
`;

const DeleteButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  cursor: pointer;
  color: #c62828;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #ffebee;
    border-color: #ef9a9a;
  }
`;

const SyncStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  color: ${props => props.$synced ? '#4CAF50' : '#ff9800'};
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 95%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.25rem 0;
  color: #333;
  font-size: 1.3rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FullWidth = styled.div`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;


const Select = styled.select`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &.primary {
    background-color: #4CAF50;
    color: white;
    
    &:hover {
      background-color: #45a049;
    }
  }
  
  &.secondary {
    background-color: #f5f5f5;
    color: #333;
    
    &:hover {
      background-color: #e0e0e0;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1.5rem 0;
  padding: 0.75rem;

  @media (min-width: 768px) {
    gap: 1rem;
    margin: 2rem 0;
    padding: 1rem;
  }
`;

const PaginationButton = styled.button`
  padding: 0.4rem 0.75rem;
  border: 1px solid #ddd;
  background: ${props => props.$active ? '#4CAF50' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.85rem;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#45a049' : '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
`;

const PageInfo = styled.span`
  color: #666;
  font-size: 0.8rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ClientsInfo = styled.div`
  text-align: center;
  padding: 0.5rem;
  color: #666;
  font-size: 0.9rem;
  border-top: 1px solid #e0e0e0;
  margin-top: 1rem;
`;

const Clients = () => {
  // La página y el texto buscado viven en la URL (?page=35&q=...) para que al refrescar
  // o volver atrás se conserve donde estaba el usuario
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(Math.max(1, parseInt(searchParams.get('page'), 10) || 1));
  const [itemsPerPage] = useState(12); // Mostrar 12 clientes por página
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    street2: '',
    city: '',
    zip: '',
    taxId: '',
    clientType: 'particular',
    marginPercentage: 0,
    habitualDiscount: 0
  });

  const queryClient = useQueryClient();

  const { data: clients, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: apiService.getClients,
  });

  const createClientMutation = useMutation({
    mutationFn: apiService.createClient,
    onSuccess: (client) => {
      queryClient.invalidateQueries(['clients']);
      // "Creado", o aviso amarillo si NO se creó porque ya había una ficha con ese teléfono/nombre
      avisarAltaCliente(toast, client);
      if (client?.reused) setSearchTerm(client.name); // que el operario vea la ficha que se ha usado
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      mostrarErrorAltaCliente(toast, error);
    }
  });

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }) => clientService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      toast.success('Cliente actualizado');
      setShowModal(false);
      setEditingClient(null);
      resetForm();
    },
    onError: () => {
      toast.error('Error al actualizar el cliente');
    }
  });

  const deleteClientMutation = useMutation({
    mutationFn: (id) => clientService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      toast.success('Cliente eliminado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el cliente');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      street: '',
      street2: '',
      city: '',
      zip: '',
      taxId: '',
      clientType: 'particular',
      marginPercentage: 0,
      habitualDiscount: 0
    });
  };

  const handleClientTypeChange = (newType) => {
    if (newType === 'particular') {
      setFormData({ ...formData, clientType: 'particular', marginPercentage: 0 });
    } else {
      setFormData({ ...formData, clientType: 'profesional', marginPercentage: formData.marginPercentage === 0 ? 100 : formData.marginPercentage });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { clientType: _tipoDerivadoEnBackend, ...datos } = formData;
    const payload = {
      ...datos,
      name: formData.name?.trim() || '',
      email: formData.email?.trim() || '',
      phone: formData.phone?.trim() || '',
      street: formData.street?.trim() || '',
      street2: formData.street2?.trim() || '',
      city: formData.city?.trim() || '',
      zip: formData.zip?.trim() || '',
      taxId: formData.taxId?.trim() || ''
    };

    if (!payload.phone) {
      toast.error('El teléfono es obligatorio');
      return;
    }

    if (editingClient) {
      updateClientMutation.mutate({ id: editingClient.id, data: payload });
    } else {
      createClientMutation.mutate(payload);
    }
  };

  const handleEdit = (client) => {
    const isPro = client.clientType === 'profesional';
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      street: client.street || '',
      street2: client.street2 || '',
      city: client.city || '',
      zip: client.zip || '',
      taxId: client.taxId || '',
      clientType: isPro ? 'profesional' : 'particular',
      marginPercentage: client.marginPercentage ?? 0,
      habitualDiscount: client.habitualDiscount ?? 0
    });
    setShowModal(true);
  };

  const handleDelete = (client) => {
    if (window.confirm(`¿Eliminar el cliente "${client.name}"? Esta acción no se puede deshacer.`)) {
      deleteClientMutation.mutate(client.id);
    }
  };

  const handleSyncOdoo = async () => {
    setIsSyncing(true);
    try {
      // Paso 1: Sincronizar clientes locales hacia Odoo (enviar)
      const sendResponse = await api.post('/odoo-sync/sync-clients-to-odoo');
      let sentCount = 0;
      if (sendResponse.data.success) {
        sentCount = sendResponse.data.stats.synced;
        if (sentCount > 0) {
          console.log(`${sentCount} clientes locales enviados a Odoo`);
        }
      }

      // Paso 2: Sincronizar contactos desde Odoo hacia local (recibir)
      const receiveResponse = await api.post('/odoo-sync/sync-contacts', {
        updateExisting: true,
        onlyNew: false,
        limit: 2000 // cubre todos los contactos activos de Odoo (antes 500 dejaba fuera ~190)
      });

      let productsStats = null;

      // Paso 3: Sincronizar productos desde Odoo hacia local
      try {
        const productsResponse = await api.post('/odoo-sync/sync-products', {
          updateExisting: true,
          onlyNew: false,
          limit: 1000
        });

        if (productsResponse.data.success) {
          productsStats = productsResponse.data.stats;
          console.log('Productos sincronizados:', productsStats);
        }
      } catch (productsError) {
        console.error('Error syncing products:', productsError);
        // No bloqueamos la sincronización de clientes si falla productos
      }

      if (receiveResponse.data.success) {
        const stats = receiveResponse.data.stats;

        // Mensaje combinado con ambas sincronizaciones
        const messages = [];
        if (sentCount > 0) {
          messages.push(`${sentCount} clientes enviados a Odoo`);
        }
        if (stats.created > 0) {
          messages.push(`${stats.created} clientes creados desde Odoo`);
        }
        if (stats.updated > 0) {
          messages.push(`${stats.updated} clientes actualizados desde Odoo`);
        }

        // Agregar info de productos si se sincronizaron
        if (productsStats) {
          if (productsStats.created > 0) {
            messages.push(`${productsStats.created} productos creados`);
          }
          if (productsStats.updated > 0) {
            messages.push(`${productsStats.updated} productos actualizados`);
          }
        }

        const message = messages.length > 0
          ? `Sincronización completada: ${messages.join(', ')}`
          : 'Sincronización completada: todo actualizado';

        toast.success(message);
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['products']); // Invalidar también productos
      } else {
        toast.error('Error durante la sincronización desde Odoo');
      }
    } catch (error) {
      console.error('Error syncing with Odoo:', error);
      toast.error(`Error de sincronización: ${error.response?.data?.details || error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Búsqueda tolerante: ignora mayúsculas, acentos y la ñ (solo al comparar; el nombre
  // se muestra tal cual), recorta espacios sobrantes y busca también por teléfono
  // comparando solo dígitos. Sin esto, "unai ibanez" o "Unai Iba " (con espacio al
  // final) no encontraban a UNAI IBAÑEZ y el operario acababa creando la ficha otra vez.
  const filteredClients = filterClients(clients, searchTerm, ['name', 'email', 'taxId']);

  // Calcular paginación
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstItem, indexOfLastItem);

  // Resetear a página 1 cuando el usuario cambia el filtro (no al cargar la página con ?q=)
  const prevSearchTerm = useRef(searchTerm);
  useEffect(() => {
    if (prevSearchTerm.current !== searchTerm) {
      prevSearchTerm.current = searchTerm;
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Si la página guardada se pasa del total (p. ej. tras borrar clientes), ir a la última
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Reflejar página y búsqueda en la URL
  useEffect(() => {
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
  }, [currentPage, searchTerm, setSearchParams]);

  if (isLoading) {
    return (
      <ClientsContainer>
        <LoadingState>Cargando clientes...</LoadingState>
      </ClientsContainer>
    );
  }

  if (error) {
    toast.error('Error al cargar los clientes');
    return (
      <ClientsContainer>
        <EmptyState>Error al cargar los clientes</EmptyState>
      </ClientsContainer>
    );
  }

  return (
    <ClientsContainer>
      <Header>
        <Title>Clientes</Title>
        <HeaderControls>
          <SearchBar
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <ButtonsRow>
            <AddButton onClick={() => { setEditingClient(null); resetForm(); setShowModal(true); }}>
              + Nuevo Cliente
            </AddButton>
            <SyncButton onClick={handleSyncOdoo} disabled={isSyncing}>
              <FaSync className={isSyncing ? 'spinning' : ''} />
              {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
            </SyncButton>
          </ButtonsRow>
        </HeaderControls>
      </Header>

      {filteredClients.length === 0 ? (
        <EmptyState>
          {searchTerm ? 'No se encontraron clientes' : 'No hay clientes registrados'}
        </EmptyState>
      ) : (
        <>
          <ClientsInfo>
            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredClients.length)} de {filteredClients.length} clientes
          </ClientsInfo>
          <ClientsGrid>
            {currentClients.map(client => {
              const isProfessional = client.clientType === 'profesional';
              return (
            <ClientCard key={client.id}>
              <ClientHeader>
                <ClientName>{client.name || 'Cliente sin nombre'}</ClientName>
                <BadgesContainer>
                  <ClientTypeBadge $isProfessional={isProfessional}>
                    {isProfessional ? 'Profesional' : 'Particular'}
                  </ClientTypeBadge>
                  <MarginBadge>{(client.marginPercentage ?? 0)}% margen</MarginBadge>
                </BadgesContainer>
              </ClientHeader>
              <ClientInfo>
                <strong>Email:</strong> {client.email}
              </ClientInfo>
              <ClientInfo>
                <strong>Teléfono:</strong> {client.phone}
              </ClientInfo>
              <ClientInfo>
                <strong>NIF/CIF:</strong> {client.taxId}
              </ClientInfo>
              {client.street && (
                <ClientInfo>
                  <strong>Dirección:</strong> {[client.street, client.street2, client.city, client.zip].filter(Boolean).join(', ')}
                </ClientInfo>
              )}
              {client.odooId && (
                client.odooArchived ? (
                  <SyncStatus $synced={false} title="El contacto existe en Odoo pero está archivado: no aparece en sus listas hasta que se desarchive">
                    ⚠ Archivado en Odoo
                  </SyncStatus>
                ) : (
                  <SyncStatus $synced={true}>
                    ✓ Sincronizado con Odoo
                  </SyncStatus>
                )
              )}
              <CardActions>
                <EditButton onClick={() => handleEdit(client)}>
                  <FaEdit /> Editar
                </EditButton>
                <DeleteButton onClick={() => handleDelete(client)}>
                  <FaTrash />
                </DeleteButton>
              </CardActions>
            </ClientCard>
          );
          })}
        </ClientsGrid>
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
              >
                Primera
              </PaginationButton>
              <PaginationButton 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
              >
                ← Anterior
              </PaginationButton>
              
              {/* Mostrar números de página */}
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <PaginationButton
                    key={pageNum}
                    $active={pageNum === currentPage}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PaginationButton>
                );
              })}
              
              <PageInfo>
                Página {currentPage} de {totalPages}
              </PageInfo>
              
              <PaginationButton 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                disabled={currentPage === totalPages}
              >
                Siguiente →
              </PaginationButton>
              <PaginationButton 
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages}
              >
                Última
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
      )}

      {showModal && (
        <Modal onClick={() => { setShowModal(false); setEditingClient(null); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</ModalTitle>
            <Form onSubmit={handleSubmit}>
              {editingClient?.odooArchived && (
                <div style={{ gridColumn: '1 / -1', background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '6px', padding: '0.6rem 0.8rem', color: '#e65100', fontSize: '0.875rem' }}>
                  ⚠ Este contacto está <strong>archivado en Odoo</strong> (nº {editingClient.odooId}). Desarchívalo en Odoo (Contactos → filtro "Archivado") para que vuelva a aparecer allí.
                </div>
              )}
              {/* Fila 1: Nombre | Teléfono */}
              <FormGroup>
                <Label>Nombre *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Teléfono *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </FormGroup>

              {/* Fila 2: Email | NIF/CIF */}
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>NIF/CIF</Label>
                <Input
                  type="text"
                  value={formData.taxId}
                  onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                />
              </FormGroup>

              {/* Fila 3: Dirección (full width) */}
              <FullWidth>
                <FormGroup>
                  <Label>Dirección</Label>
                  <Input
                    type="text"
                    placeholder="Calle principal"
                    value={formData.street}
                    onChange={(e) => setFormData({...formData, street: e.target.value})}
                  />
                </FormGroup>
              </FullWidth>

              {/* Fila 4: Ciudad | Código Postal */}
              <FormGroup>
                <Label>Ciudad</Label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>Código Postal</Label>
                <Input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => setFormData({...formData, zip: e.target.value})}
                />
              </FormGroup>

              {/* Fila 5 (solo al editar): Tipo de cliente | Margen. Un cliente nuevo nace siempre
                  particular con margen 0; si hace falta, se cambia después desde Editar. */}
              {editingClient && (
                <>
                  <FormGroup>
                    <Label>Tipo de cliente</Label>
                    <Select
                      value={formData.clientType}
                      onChange={(e) => handleClientTypeChange(e.target.value)}
                    >
                      <option value="profesional">Profesional</option>
                      <option value="particular">Particular</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Margen (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="500"
                      value={formData.marginPercentage}
                      onChange={(e) => setFormData({...formData, marginPercentage: parseInt(e.target.value) || 0})}
                      disabled={formData.clientType === 'particular'}
                    />
                  </FormGroup>
                </>
              )}

              {/* Fila 6: Descuento habitual (solo informativo) */}
              <FormGroup>
                <Label>Descuento habitual (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.habitualDiscount}
                  onChange={(e) => setFormData({...formData, habitualDiscount: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                />
                <small style={{ color: '#888', fontSize: '0.72rem', marginTop: '2px' }}>Solo informativo, no afecta a los cálculos.</small>
              </FormGroup>

              {/* Botones (full width) */}
              <FullWidth>
                <ButtonGroup>
                  <Button type="button" className="secondary" onClick={() => { setShowModal(false); setEditingClient(null); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="primary">
                    {editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
                  </Button>
                </ButtonGroup>
              </FullWidth>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </ClientsContainer>
  );
};

export default Clients;
